import { KalturaPurchaseStatus } from '@kaltura-ott/ott-client-types/api/types';
import { KalturaRuleType } from '@kaltura-ott/ott-client-types/api/types/KalturaRuleType';
import {
  AssetPresentationRatioEnum,
  EpgRequestArgs,
  getEpgDataV2,
  getRatioByType,
  KuxFilteredCategoriesDTO,
  MediaType,
  MAX_EPG_PERIOD,
  startOfDay,
  UIDAPresentationTypeEnum,
  UIDAStructureTypeEnum,
  startPartOfDay,
  endPartOfDay,
  addChannelStats,
  PREDEFINED_REFERENCES,
  CategoryUidaAsset,
} from '@kaltura-ott/tvpil';
import { action, computed, observable, when } from 'mobx';
import { lastValueFrom } from 'rxjs';
import { Subject } from 'rxjs';
import { container } from 'tsyringe';

import { fetchChannelCollection } from '../../../rhino';
import { ChannelsPersonalization } from '../../../rhino/data/channel/personalization';
import { fetchChannelsPersonalization } from '../../../rhino/data/channel/personalization/fetchChannelsPersonalization';
import { getChannelsSettings } from '../../../rhino/data/channel/settings';
import { queryClient } from '../../../rhino/data/queryClient';
import { queryKeys } from '../../../rhino/data/queryKeys';
import { featureEnabler } from '../../../utils';
import { CHANNELS_COLLECTION_FETCH_STALE_TIME } from '../../../utils/constants';
import { convertRhinoErrorToTvpil } from '../../../utils/error';
import { errorHandling } from '../../../utils/error-handling';
import type { AppStore } from '../../app/app-store';
import { channelStats } from '../../channelStats';
import type { LastWatchedLinearStore } from '../../channelStats/last-watched-linear-store';
import type { DmsStore } from '../../dms/dms-store';
import { Linear, ProgramPlaceholder } from '../../entities';
import { creatorEntityStore } from '../../entities/entities.service';
import type { Program } from '../../entities/Program';
import { ErrorPriority } from '../../error';
import type { FavoriteRepository } from '../../favoriteRepository';
import type { LinearRepository } from '../../linearRepository/linear-repository';
import type { ParentalPinStore } from '../../parentalPin';
import type { TransactionStore } from '../../transaction/transaction-store';
import { CategoryChildren } from '../category-children';
import { CategoryStore } from '../category-store';
import { composeLinears } from '../composeLinears';

import { generateEmptyProgramForDay } from './epg-store.service';

export interface EpgFilter {
  id: number;
  name: string;
}

type ImageOptions = {
  type: number;
  ratio?: AssetPresentationRatioEnum;
};

export interface EpgFilter {
  id: number;
  name: string;
}

export interface EpgInitArgs {
  currentFilter?: EpgFilter;
}

export interface InnerEpgData {
  [timestamp: string]: Program[] | ProgramPlaceholder[];
}

const PLACEHOLDER_LINEAR_LENGTH = 10; // 90 min

/**
 * Epg page category store
 * @category Store
 */
export class EpgStore extends CategoryStore<CategoryChildren> {
  static moduleName = 'EpgStore';

  public type: UIDAStructureTypeEnum.page = UIDAStructureTypeEnum.page;
  public subtype: UIDAPresentationTypeEnum.epg = UIDAPresentationTypeEnum.epg;

  static hoursDownload = 24;
  static primeTime?: number;
  static epgScreenLength?: number;
  static skipChannelPlaceholdersGeneration = false;

  //[Restricted to 7 and must be lower or equal then rolling buffer configuration]
  public maxEpgDateBackward = MAX_EPG_PERIOD;
  //[Restricted to 7 and must be lower then ingested programs dates]
  public maxEpgDateForward = MAX_EPG_PERIOD;
  public filters: EpgFilter[];
  public primeTime?: number;
  public imageRatio: AssetPresentationRatioEnum;
  public imageOptions?: ImageOptions;
  public link: string;
  @observable public currentFilter?: EpgFilter;
  @observable public lastUpdateTime = Date.now();

  @observable.ref private innerTvChannels: Array<Linear> = [];
  private personalization: ChannelsPersonalization | undefined = undefined;
  private linears: Array<Linear> = [];
  private sortedCollection: Array<Linear> = [];

  private mostWatchedParams: KuxFilteredCategoriesDTO['mostWatched'];
  private loadMap = new Map<number, Set<number>>(); // linearId:timestamp[]

  private lastWatchedLinear: LastWatchedLinearStore;
  private tvChannelsLoadSubject = new Subject();
  private programsLoadSubject: { [tvChannelId: number]: { [startTimestamp: number]: Subject<unknown> } } = {};
  private innerEpgData = new Map<number, InnerEpgData>();
  @observable private visible = false;

  constructor(
    categoryTree: KuxFilteredCategoriesDTO,
    parentCategoryTree?: KuxFilteredCategoriesDTO,
    children: CategoryChildren[] = [],
  ) {
    super(categoryTree, parentCategoryTree);

    this.lastWatchedLinear = container.resolve<LastWatchedLinearStore>('lastWatchedLinearStore');

    this.imageRatio = categoryTree.epgProperty.presentationRatio;
    this.children = children;

    this.maxEpgDateForward = categoryTree.epgProperty.maxEpgDateForward;
    this.maxEpgDateBackward = categoryTree.epgProperty.maxEpgDateBackward;

    if (categoryTree.epgProperty.assetPresentationImageType) {
      this.imageOptions = {
        type: categoryTree.epgProperty.assetPresentationImageType,
        ratio: getRatioByType(categoryTree.epgProperty.assetPresentationImageType),
      };
    }
    this.filters = categoryTree.epgProperty.filters;
    this.link = `/page/${this.referenceId}`;

    this.primeTime = categoryTree.epgProperty.primeTime;
    EpgStore.primeTime = categoryTree.epgProperty.primeTime;
    this.mostWatchedParams = categoryTree.mostWatched;
  }

  private get transactionStore() {
    return container.resolve<TransactionStore>('transactionStore');
  }

  public get presentFavoriteIndicator() {
    const appStore = container.resolve<AppStore>('appStore');

    return (
      appStore.rootStore.isFavoriteChannelPersonalization && this.categoryTree.railProperty.presentFavoriteIndicator
    );
  }

  /**
   * Initializes store. Must be called when opening an EPG page or changing filter
   *
   * @param currentFilter
   * @param preload
   */
  @action
  @errorHandling({
    callback: function handleEpgInitError(this: EpgStore, error) {
      convertRhinoErrorToTvpil(error);
    },
    type: ErrorPriority.high,
  })
  public async init(
    { currentFilter = this.filters[0] }: EpgInitArgs = {},
    preload = false,
    skipGeneratePlaceholders = false,
  ): Promise<void> {
    if (!this.visible && this.innerTvChannels.length && !this.innerTvChannels[0]?.data.isPlaceholder) {
      await this.applyPersonalizationInternal();
    }

    if (!preload) {
      const parentalPinStore = container.resolve<ParentalPinStore>('parentalPinStore');
      parentalPinStore.setLastPageLink(this.referenceId);
      this.lastWatchedLinear.clearCurrentHeroRail();
    }

    if (this.currentFilter && this.currentFilter.id === currentFilter.id) {
      this.setVisible(preload);
      return;
    }

    this.resetStore();

    this.currentFilter = currentFilter;
    if (!EpgStore.skipChannelPlaceholdersGeneration) {
      this.generatePlaceholderLinear();
    }

    try {
      await this.loadTvChannels(this.currentFilter);
    } catch (e) {
      this.currentFilter = undefined;
      throw e;
    }

    this.setVisible(preload);
  }

  private hasPersonalizationChanged(
    currentPersonalization: ChannelsPersonalization,
    nextPersonalization: ChannelsPersonalization,
  ) {
    const isFavoritePersonalization =
      'favoriteChannels' in currentPersonalization && 'favoriteChannels' in nextPersonalization;
    const isPersonalLineup = 'personalLineup' in currentPersonalization && 'personalLineup' in nextPersonalization;

    return (
      (isFavoritePersonalization && currentPersonalization.favoriteChannels !== nextPersonalization.favoriteChannels) ||
      (isPersonalLineup && currentPersonalization.personalLineup !== nextPersonalization.personalLineup)
    );
  }

  private async applyPersonalizationInternal() {
    const personalization = await fetchChannelsPersonalization({ referenceId: this.categoryTree.referenceId });

    if (!personalization) {
      return;
    }

    if (!this.personalization || this.hasPersonalizationChanged(this.personalization, personalization)) {
      container.resolve<FavoriteRepository>('favoriteRepository').syncLocalState();

      const settings = getChannelsSettings(this.categoryTree as unknown as CategoryUidaAsset);

      if (!settings) {
        return;
      }

      this.innerTvChannels = composeLinears(this.linears, personalization, settings, this.sortedCollection);

      this.personalization = personalization;
    }
  }

  /**
   * Method should be called when exiting the epg page.
   */
  onInvisible() {
    this.visible = false;
  }

  private setVisible(preload: boolean) {
    if (!preload) {
      this.visible = true;
    }
  }
  /**
   * Returns channels for EPG.
   *
   * TODO combine logic with heroRail
   */
  @computed public get tvChannels(): Linear[] {
    if (this.innerTvChannels.length && this.innerTvChannels[0].data.isPlaceholder) return this.innerTvChannels;

    // This functionality is disabled on all platforms and environments ( https://kaltura.atlassian.net/browse/KUXEXT-19933 )
    if (featureEnabler.variations.get('PIN_LAST_WATCHED_CHANNEL_TO_THE_TOP_OF_EPG')) {
      const lastWatchedLinear = this.lastWatchedLinear?.getLastLinear(PREDEFINED_REFERENCES.home)?.linear;

      if (!this.lastWatchedLinear.isReady || !lastWatchedLinear) return this.innerTvChannels;

      // put lastWatched item first
      const isLastWatchedGeoBlocked = !!lastWatchedLinear.getRuleByType(KalturaRuleType.geo);
      if (lastWatchedLinear.isPurchased && !isLastWatchedGeoBlocked) {
        const lastWatchedLinearIndex = this.innerTvChannels.findIndex((linear) => linear.id === lastWatchedLinear.id);

        // if no Linear item in list just add it to the beginning
        if (lastWatchedLinearIndex === -1) {
          return this.innerTvChannels;
        }

        // if Linear item is already in the list cut it out and place it first
        return [
          this.innerTvChannels[lastWatchedLinearIndex],
          ...this.innerTvChannels.slice(0, lastWatchedLinearIndex),
          ...this.innerTvChannels.slice(lastWatchedLinearIndex + 1),
        ];
      }
    }

    return this.innerTvChannels;
  }

  /**
   * Clears store
   */
  @action
  public resetStore(): void {
    this.tvChannelsLoadSubject.next(null);
    this.tvChannelsLoadSubject.complete();
    this.tvChannelsLoadSubject = new Subject();
    Object.values(this.programsLoadSubject).forEach((time) => {
      Object.values(time).forEach((subject) => {
        subject.next(null);
        subject.complete();
      });
    });
    this.programsLoadSubject = {};
    this.loadMap = new Map();
    this.innerEpgData = new Map<number, InnerEpgData>();
    this.currentFilter = undefined;
    this.visible = false;
    this.personalization = undefined;
  }

  /**
   * Shows channel loading status
   *
   * @returns boolean
   */
  @computed public get isReadyTvChannel(): boolean {
    return (
      !!this.innerTvChannels.length &&
      !!this.tvChannels.length &&
      !this.innerTvChannels[0].data.isPlaceholder &&
      this.visible
    );
  }

  /**
   * Must be called when navigating by "Day bar". Aborting requests for other days
   *
   * @param date
   */
  @action
  @errorHandling({ type: ErrorPriority.medium })
  public onDayChange(date: Date): void {
    const startTimeStamp = startOfDay(date);
    Object.values(this.programsLoadSubject).forEach((timeSubjects) => {
      Object.entries(timeSubjects).forEach(([time, subject]) => {
        if (startTimeStamp.toString() !== time) {
          subject.next(null);
          subject.complete();
        }
      });
    });
  }

  /**
   * Must be called when channel is in visibility zone
   *
   * @param tvChannel
   */
  @action
  @errorHandling({ type: ErrorPriority.low })
  public setVisibleTvChannel(tvChannel: Linear, index = -1): void {
    if (!this.innerEpgData.get(tvChannel.id)) {
      if (this.innerEpgData.get(index)) {
        this.changeLinearInProgramPlaceholder(tvChannel, index);
        this.innerEpgData.delete(index);
        return;
      }
      const emptyPrograms: { [timestamp: number]: ProgramPlaceholder[] } = {};

      let timestamp = startPartOfDay(
        new Date(),
        EpgStore.hoursDownload,
        (-1 * (this.maxEpgDateBackward * 24)) / EpgStore.hoursDownload,
      );
      while (
        timestamp <
        startPartOfDay(new Date(), EpgStore.hoursDownload, (this.maxEpgDateBackward * 24) / EpgStore.hoursDownload)
      ) {
        emptyPrograms[timestamp] = generateEmptyProgramForDay(
          timestamp,
          endPartOfDay(new Date(timestamp), EpgStore.hoursDownload),
          tvChannel,
        );
        timestamp = startPartOfDay(new Date(timestamp), EpgStore.hoursDownload, 1);
      }

      this.setInnerData(tvChannel, emptyPrograms);
    }
  }

  /**
   * Must be called when channel is out of visibility zone
   *
   * @param tvChannel
   */
  @action
  public setInvisibleTvChannel(linear: Linear): void {
    Object.values(this.programsLoadSubject[linear.id] || {}).forEach((subject) => {
      subject.next(null);
      subject.complete();
    });
    if (featureEnabler.variation('REMOVE_INVISIBLE_TV_CHANNELS', false)) {
      this.innerEpgData.delete(linear.id);
      this.loadMap.delete(linear.id);
      linear.programs = [];
    }
  }

  /**
   * Must be called when program is in visibility zone
   * @param program
   */
  @action
  @errorHandling({ type: ErrorPriority.medium })
  public async setVisibleProgram(program: Program | ProgramPlaceholder, skipErrorHandling?: boolean): Promise<void> {
    if (program instanceof ProgramPlaceholder && !program.linear?.data.isPlaceholder && program.linear) {
      await this.loadPrograms(
        startPartOfDay(new Date(program.data.startDate), EpgStore.hoursDownload),
        program.linear,
        !skipErrorHandling,
      );
    }
  }

  /**
   * Must be called when program is out of visibility zone
   * @param program
   */
  @action
  public setInvisibleProgram(program: Program | ProgramPlaceholder): void {
    return;
  }

  public async preloadPrograms() {
    await when(
      () => !!this.tvChannels.length && !!this.innerTvChannels.length && !this.tvChannels[0].data.isPlaceholder,
    );
    for (let j = 0; j < Math.min(EpgStore.epgScreenLength || 0, this.innerTvChannels.length); j++) {
      this.setVisibleTvChannel(this.tvChannels[j]);
      const todayPrograms = this.innerEpgData.get(this.tvChannels[j].id)?.[
        startPartOfDay(undefined, EpgStore.hoursDownload)
      ];
      if (todayPrograms && todayPrograms.length) {
        await this.setVisibleProgram(todayPrograms[0], true);
      }
    }
  }

  public async applyPersonalization() {
    if (!this.linears.length) {
      return Promise.resolve();
    }

    return this.applyPersonalizationInternal();
  }

  private getPrograms(linear: Linear): (Program | ProgramPlaceholder)[] {
    const programs = this.innerEpgData.get(linear.id);
    if (!programs) {
      return [];
    }

    const sortedPrograms = Object.keys(programs)
      .sort((a, b) => Number(a) - Number(b))
      .reduce<(Program | ProgramPlaceholder)[]>(
        (previousValue, current) => [...previousValue, ...programs[current]],
        [],
      );

    return [...sortedPrograms] || [];
  }

  private changeLinearInProgramPlaceholder(tvChannel: Linear, index: number): void {
    const newInner: InnerEpgData = {};
    const innerData = this.innerEpgData.get(index);
    if (innerData) {
      Object.keys(innerData).map((timestamp) => {
        const inner = innerData[timestamp] as ProgramPlaceholder[];
        newInner[timestamp] = inner.map((program) => {
          program.data = { ...program.data, linearAssetId: tvChannel.id };
          return program;
        });
      });
    }

    this.setInnerData(tvChannel, newInner);
  }
  private generatePlaceholderLinear(): void {
    this.innerTvChannels = Array.from(Array(PLACEHOLDER_LINEAR_LENGTH).keys()).map((key) => {
      const linear = new Linear({
        id: key,
        images: [],
        title: '',
        adult: false,
        parentalRating: 0,
        type: 0,
        metas: {},
        tags: {},
        catchUpBuffer: 0,
        channelNumber: 0,
        description: '',
        // @ts-expect-error TODO Fix types
        price: {
          purchaseStatus: KalturaPurchaseStatus.free,
        },
        mediaType: MediaType.Linear,
        isPlaceholder: true,
      });
      this.setVisibleTvChannel(linear);
      return linear;
    });
    this.lastUpdateTime = Date.now();
  }

  @action
  private async loadTvChannels(filter: EpgFilter): Promise<void> {
    const linearsData = await queryClient.fetchQuery({
      queryKey: queryKeys.channelCollection(filter.id, this.transactionStore.isAdultEntitled, this.aggregatedKSQL),
      queryFn: () =>
        fetchChannelCollection({
          collectionId: filter.id,
          includeAdult: this.transactionStore.isAdultEntitled,
          kSql: this.aggregatedKSQL,
        }),
      staleTime: CHANNELS_COLLECTION_FETCH_STALE_TIME,
    });

    if (!linearsData.objects.length) {
      return;
    }

    const linearRepository = container.resolve<LinearRepository>('linearRepository');
    const watcheStats = channelStats.getMostWatched(
      this.mostWatchedParams.presentMostWatchedPeriod,
      this.mostWatchedParams.presentMostWatchedCount,
    );

    const linears = [];

    for (let i = 0; i < linearsData.collection.length; i++) {
      const linear = linearRepository.getLinearById(linearsData.collection[i]);

      if (!linear) {
        continue;
      }

      if (!linearRepository.isLinear(linear)) {
        continue;
      }

      addChannelStats(linear, watcheStats);

      linears.push(linear);
    }

    this.linears = linears;

    const appStore = container.resolve<AppStore>('appStore');

    if (appStore.rootStore.isFavoriteChannelPersonalization) {
      this.sortedCollection = [...linears].sort((a, b) => a.data.channelNumber - b.data.channelNumber);
    }

    if (this.linears.length === 0) {
      throw new Error('Channels was not configured!');
    }

    await this.applyPersonalizationInternal();
  }

  private async loadPrograms(startTimestamp: number, linear: Linear, handleError?: boolean): Promise<void> {
    if (this.loadMap.get(linear.id)?.has(startTimestamp)) {
      return;
    }
    this.loadMap.get(linear.id)
      ? this.loadMap.get(linear.id)?.add(startTimestamp)
      : this.loadMap.set(linear.id, new Set([startTimestamp]));

    const dmsStore = container.resolve<DmsStore>('dmsStore');

    const currentEpgData: EpgRequestArgs = {
      startTime: startTimestamp,
      endTime: endPartOfDay(new Date(startTimestamp), EpgStore.hoursDownload),
      linearIdList: [linear.id],
      loadMosaicPrograms: dmsStore.dms.mosaicInfo.isMosaicSupported,
    };

    if (!this.programsLoadSubject[linear.id]) {
      this.programsLoadSubject[linear.id] = {};
    }
    this.programsLoadSubject[linear.id][startTimestamp] = new Subject();

    try {
      const programsType = await lastValueFrom(
        getEpgDataV2(
          currentEpgData,
          undefined,
          undefined,
          undefined,
          this.programsLoadSubject[linear.id][startTimestamp],
          this.maxEpgDateBackward,
          this.maxEpgDateForward,
          handleError,
        ),
        {
          defaultValue: null,
        },
      );

      if (!programsType) {
        this.loadMap.get(linear.id)?.delete(startTimestamp);
        return;
      }
      if (!programsType.length) {
        return;
      }

      const programs = programsType.map((type) => {
        const program = creatorEntityStore(type);
        return program;
      });
      this.updateInnerEpgData(programs, startTimestamp, linear);
    } catch (e) {
      this.loadMap.get(linear.id)?.delete(startTimestamp);

      if (handleError) {
        throw e;
      }
    }
  }

  private updateInnerEpgData(programs: Program[], startTimestamp: number, linear: Linear) {
    const prevStartTimestamp = startPartOfDay(new Date(startTimestamp), EpgStore.hoursDownload, -1);
    const nextStartTimestamp = startPartOfDay(new Date(startTimestamp), EpgStore.hoursDownload, 1);

    const programsMap = this.innerEpgData.get(linear.id);
    let prevPrograms = programsMap ? programsMap[prevStartTimestamp] : undefined;
    let nextPrograms = programsMap ? programsMap[nextStartTimestamp] : undefined;
    if (prevPrograms && programsMap) {
      if (!prevPrograms[0]?.data.title) {
        prevPrograms = generateEmptyProgramForDay(prevPrograms[0].data.startDate, programs[0].data.startDate, linear);
      } else {
        if (programs[0].data.id === prevPrograms[prevPrograms.length - 1].data.id) {
          prevPrograms.pop();
        }
      }
      programsMap[prevStartTimestamp] = prevPrograms;
    }
    if (nextPrograms && programsMap) {
      if (!nextPrograms[0]?.data.title) {
        nextPrograms = generateEmptyProgramForDay(
          programs[programs.length - 1].data.endDate,
          nextPrograms[nextPrograms.length - 1].data.endDate,
          linear,
        );
      } else {
        if (programs[programs.length - 1].data.id === nextPrograms[0].data.id) {
          nextPrograms.shift();
        }
      }
      programsMap[nextStartTimestamp] = nextPrograms;
    }
    if (programsMap) {
      programsMap[startTimestamp] = programs;
      this.setInnerData(linear as Linear, programsMap);
    }
  }

  private setInnerData(linear: Linear, programsMap: InnerEpgData) {
    this.innerEpgData.set(linear.id, { ...programsMap });
    linear.programs = this.getPrograms(linear);
    this.lastUpdateTime = Date.now();
  }
}
